import { AbstractInterface } from './AbstractInterface'
import { IACCommunication, IACHub } from '..'
import { Toolbar, ToolbarUseCase } from '../navigator-services/Toolbar'
import { IACMessageData } from '../transport/iacProtocol'

/**
 * Fired after trying to create and dispatch an IAC use case from the data provided by the mailcheck extension (AKA
 * toolbar)
 */
export interface ToolbarDataEvent extends CustomEvent {
  detail: {
    payload: {
      id: number,
      success: boolean,
      error_code: string,
      error_message: string,
    };
  }
}

/*
 * Toolbar support
 *
 * Comment from CH:
 * As far as I know the toolbar support works as described below.
 *
 * The communication between the Toolbar and the navigator client (JavaScript client)
 * works by setting a IAC use caste to a DOM attribute before a custom DOM event is
 * triggered by the Toolbar. When the event is triggered from the Toolbar, the navigator
 * client reads the content form the DOM attribute and creates an IAC use case from it and
 * sends this use case to the target app with the IACHub.
 *
 * The DOM element which contains the attribute must have the id 'unitedinternet_toolbar',
 * so that the Toolbar can find it. The attribute name is 'event_data_to_page' and the
 * content is an IAC use case encoded with Base64. The DOM element will be rendered with
 * the mailcheck component.
 *
 * The custom event has the name 'unitedinternet_toolbar_start_usecase' and has no content.
 * (no custom data as payload)
 *
 * The 'normal' communication only works when the user is logged in. When the user
 * is not logged in the first event has to be faked by the navigator server and client.
 *
 * This is done via a parameter which is added by the Toolbar. The Toolbar uses
 * the partner data for that. The correct behavior can be explained by the navigator team.
 * For us, it is important to know that in this case the navigator server adds the use case
 * to the app-config. Which is than manually added to the DOM attribute by the mailcheck
 * component. The third-party-support than calls the 'navigatorStartUp' to manually trigger
 * the 'unitedinternet_toolbar_start_usecase'.
 *
 * There is also a communication back from the navigator to the Toolbar. The communication
 * is done when the 'unitedinternet_toolbar_start_usecase' is handled then a
 * 'unitedinternet_toolbar_start_usecase_response' is sent to the Toolbar which
 * identify if the triggered IAC use case could be handled or not.
 */
export class ToolbarInterface extends AbstractInterface {

  private static $window: Window;

  public static init(): void {
    this.$window = window;
    this.$window.addEventListener('unitedinternet_toolbar_start_usecase', () => this.handleToolbarUsecase());
  }

  private static handleToolbarUsecase() {
    try {
      const $toolbar = document.getElementById('unitedinternet_toolbar');
      const payload = $toolbar.getAttribute('event_data_to_page');

      if (!payload) {
        this.responseFailure(undefined, 'BAD_REQUEST', 'No payload property defined');
        return;
      }

      const data: ToolbarUseCase = JSON.parse(atob(decodeURIComponent(payload)));
      if (!(typeof data === 'object') || !data.id || !data.usecase || !data.caller_app || !data.caller_version) {
        this.responseFailure(((typeof data === 'object') &&
          data.id) ? data.id : undefined, 'BAD_REQUEST', 'Invalid data structure');
        return;
      }

      data.args = data.args || [{}];

      /* istanbul ignore next if */
      if (!data.args.length) {
        data.args.push({});
      }

      // FIXME: deprecated [POS-1494]
      // The use case is deprecated, but sill used
      /* istanbul ignore next if */
      if (data.usecase === 'openmail') {
        data.usecase = 'open_mail';
      }

      if (!IACCommunication.hasUsecase(data.usecase as string) &&
        !IACCommunication.hasUsecase((data.usecase as IACMessageData).usecase)
      ) {
        this.responseFailure(data.id, 'UNKNOWN_USECASE', `Usecase ${data.usecase} does not exist.`);
        return;
      }

      IACHub.handle(Toolbar.iacUsecaseFactory(data));

      this.responseSuccess(data.id);
    } catch (e) {
      this.responseFailure(0, 'INTERNAL_ERROR', 'An unknown error occurred.');
    }
  }

  private static responseSuccess(id: number) {
    this.dispatchDataEvent('unitedinternet_toolbar_start_usecase_response', {
      payload: {
        id,
        success: true,
        error_code: '',
        error_message: '',
      },
    });
  }

  private static responseFailure(id: number, errorCode: string, errorMsg: string) {
    this.dispatchDataEvent('unitedinternet_toolbar_start_usecase_response', {
      payload: {
        id,
        success: false,
        error_code: errorCode,
        error_message: errorMsg,
      },
    });
  }

  private static dispatchDataEvent(type: string, data: ToolbarDataEvent['detail']) {
    let event = null;

    /* istanbul ignore next else */
    if (typeof (this.$window as any).CustomEvent === 'function') {
      // DOM L4 event constructor pattern for modern browsers also Edge
      event = new CustomEvent(type, {
        detail: data
      });
    } else {
      // IE9 till IE11: DOM L3 createEvent()/initCustomEvent() constructor pattern
      event = document.createEvent('CustomEvent');
      event.initCustomEvent(type, true, true, data);
    }

    this.$window.dispatchEvent(event);
  }

  private static dispatchEvent(type: string) {
    let event = null;

    /* istanbul ignore next else */
    if (typeof (this.$window as any).Event === 'function') {
      // DOM L4 event constructor pattern for modern browsers also Edge
      event = new Event(type);
    } else {
      // IE9 till IE11: DOM L3 createEvent()/initEvent() constructor pattern
      event = document.createEvent('Event');
      event.initEvent(type, true, true);
    }

    this.$window.dispatchEvent(event);
  }

  /*
   * Will be called by the Navigator on startup
   */
  public static navigatorStartUp(): void {
    // We have to trigger the event asynchronous, because the old implementation
    // access directly to the DOM element with the id 'unitedinternet_toolbar'
    // to get the payload for the toolbar. But the element will be rendered with
    // angular and this is done asynchronous [POS-1351]
    window.setTimeout(() => {
      this.dispatchEvent('unitedinternet_toolbar_start_usecase');
    }, 0);
  }

  /**
   * Returns a promise that will resolve when the toolbar use case has been processed
   */
  public static toolbarStartupPromise(): Promise<ToolbarDataEvent['detail']> {
    return new Promise((resolve) => {
      const callback = (ev: ToolbarDataEvent) => {
        window.removeEventListener('unitedinternet_toolbar_start_usecase_response', callback)
        resolve(ev.detail)
      }
      window.addEventListener('unitedinternet_toolbar_start_usecase_response', callback)
      this.navigatorStartUp()
    })
  }

}
